import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { Modal, Spinner } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { Icon } from '@iconify/react/dist/iconify.js';
import api from '../../services/api';
import { toast } from 'react-toastify';
import ViewCollectionRequestModal from './ViewCollectionRequestModal';

const CollectionRequestsTable = () => {
  const [data, setData] = useState([]);

  // For searching by broker name or broker mobile
  const [filterText, setFilterText] = useState('');

  // Modals and states
  const [currentRequest, setCurrentRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Approve modal input
  const [transactionNumber, setTransactionNumber] = useState('');

  // Decline modal input
  const [declineReason, setDeclineReason] = useState('');

  // Fetch collection requests on mount
  useEffect(() => {
    fetchCollectionRequests();
  }, []);

  const fetchCollectionRequests = async () => {
    try {
      const response = await api.get('/transaction/unpaginated?inOrOut=out&status=pending');
      // Adjust if your API response is nested differently
      setData(response.data?.response?.data || []);
    } catch (error) {
      console.error('Error fetching collection requests:', error);
    }
  };

  // --- View Handlers ---
  const handleView = (request) => {
    setCurrentRequest(request);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setCurrentRequest(null);
    // Optionally refetch if the view modal might cause data changes
    fetchCollectionRequests();
  };

  // --- Approve Handlers ---
  const openApproveModal = (request) => {
    setCurrentRequest(request);
    setShowApproveModal(true);
  };

  const closeApproveModal = () => {
    setShowApproveModal(false);
    setTransactionNumber('');
    setCurrentRequest(null);
  };

  const confirmApprove = async () => {
    setLoading(true);
    try {
      const payload = {
        transactionNumber,
        transaction_id: currentRequest.id,
      };
      await api.post(`/wallet/approveWithdrawal`, payload);
      toast.success('Request Approved Successfully!');
      closeApproveModal();
      // Refresh data so the table updates
      fetchCollectionRequests();
    } catch (error) {
      toast.error('Failed to Approve Request');
      console.error(error);
    }
    setLoading(false);
  };

  // --- Decline Handlers ---
  const openDeclineModal = (request) => {
    setCurrentRequest(request);
    setShowDeclineModal(true);
  };

  const closeDeclineModal = () => {
    setShowDeclineModal(false);
    setDeclineReason('');
    setCurrentRequest(null);
  };

  const confirmDecline = async () => {
    setLoading(true);
    try {
      // If your endpoint needs the request's ID, include it here
      // For example: { reason: declineReason, transaction_id: currentRequest.id }
      await api.post(`/wallet/rejectWithdrawal`, { 
        reason: declineReason,
        transaction_id: currentRequest.id  // Add this line

       });
      toast.success('Request Declined Successfully!');
      closeDeclineModal();
      // Refresh data so the table updates
      fetchCollectionRequests();
    } catch (error) {
      toast.error('Failed to Decline Request');
      console.error(error);
    }
    setLoading(false);
  };

  // Define the table columns
  const columns = [
    {
      name: 'Broker Name',
      selector: (row) => row.broker?.name ?? '',
      sortable: true,
    },
    {
      name: 'Broker Number',
      selector: (row) => row.broker?.mobile ?? '',
      sortable: true,
    },
    {
      name: 'Reason',
      selector: (row) => row.type,
      sortable: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="d-flex">
          {/* View Button */}
          <button
            onClick={() => handleView(row)}
            className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
          >
            <Icon icon="iconamoon:eye-light" />
          </button>
          {/* Approve Button */}
          <button
            onClick={() => openApproveModal(row)}
            className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
          >
            <Icon icon="mdi:check-circle-outline" />
          </button>
          {/* Decline Button */}
          <button
            onClick={() => openDeclineModal(row)}
            className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
          >
            <Icon icon="mdi:close-circle-outline" />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  // CSV headers
  const csvHeaders = [
    { label: 'Broker Name', key: 'broker.name' },
    { label: 'Broker Number', key: 'broker.mobile' },
    { label: 'Reason', key: 'type' },
  ];

  // Filter logic – you can customize this as needed
  // For instance, we check broker name, broker number, or reason
  const filteredData = data.filter((item) => {
    const brokerName = item.broker?.name?.toLowerCase() || '';
    const brokerMobile = item.broker?.mobile?.toLowerCase() || '';
    const reason = (item.type || '').toLowerCase();
    const search = filterText.toLowerCase();
    return (
      brokerName.includes(search) ||
      brokerMobile.includes(search) ||
      reason.includes(search)
    );
  });

  // Custom styles to make text larger if desired
  const customStyles = {
    rows: {
      style: {
        fontSize: '15px',
      },
    },
  };

  return (
    <div className="card basic-data-table px-3 py-3">
      <div className="card-body">
        {/* Filters and Export */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex gap-2">
            {/* Text filter */}
            <input
              type="text"
              placeholder="Search by broker name, number, or reason"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="form-control"
              style={{ width: '250px' }}
            />
          </div>

          {/* CSV Export Button */}
          <CSVLink
            data={filteredData}
            headers={csvHeaders}
            filename="collection-requests.csv"
            className="btn btn-secondary"
          >
            Export CSV
          </CSVLink>
        </div>

        {/* DataTable */}
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          highlightOnHover
          responsive
          striped
          customStyles={customStyles}
          noDataComponent="No results"
        />
      </div>

      {/* View Request Modal */}
      {showViewModal && (
        <ViewCollectionRequestModal
          show={showViewModal}
          handleClose={handleCloseViewModal}
          collectionRequest={currentRequest}
        />
      )}

      {/* Approve Modal */}
      <Modal show={showApproveModal} onHide={closeApproveModal}>
        <Modal.Header closeButton>
          <Modal.Title>Approve Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group">
            <label>Transaction Number</label>
            <input
              type="text"
              value={transactionNumber}
              onChange={(e) => setTransactionNumber(e.target.value)}
              className="form-control"
              placeholder="Enter transaction number"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary" onClick={confirmApprove} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Confirm'}
          </button>
          <button className="btn btn-secondary" onClick={closeApproveModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      {/* Decline Modal */}
      <Modal show={showDeclineModal} onHide={closeDeclineModal}>
        <Modal.Header closeButton>
          <Modal.Title>Decline Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group">
            <label>Reason for Declining</label>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="form-control"
              rows="3"
              placeholder="Enter a reason for declining"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-danger" onClick={confirmDecline} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Confirm Decline'}
          </button>
          <button className="btn btn-secondary" onClick={closeDeclineModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CollectionRequestsTable;
